import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Info } from 'lucide-react';
import { Alert, AlertDescription } from '@design-system';
import { CurrencyInput, PercentInput } from '..';
import {
  selectClient,
  putMultipleFundingSourceForClientByClientIdRequest,
  selectSession,
  selectSessionAdvisor,
} from '../model';
import { FormErrorAlert } from './FormErrorAlert';
import { isNullOrUndefined } from '@shared';
import { formatCompactCurrency } from '@client/util/formatCompactCurrency';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { SelfFundingField } from './SelfFundingField';
import { SessionType } from '@shared';
import { DialogModal } from './DialogModal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';

interface SelfFundingDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundingSourceId?: string;
}

type SelfFundingKey = keyof SelfFundingSource;

const ErrorMessage = ({ message }: { message?: string }) => {
  if (!message) {
    return null;
  } else {
    return <p className="ml-2 text-sm text-red-400">{message}</p>;
  }
};

export function SelfFundingDetailsModal({
  open,
  setOpen,
  fundingSourceId,
}: SelfFundingDetailsModalProps) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    fundingSources,
    clientId,
    multipleFundingSources,
    intakeSurvey: { clientHasStartedLtc, clientBirthDate },
    appliedInferenceSet: { ltcAtYear, yearsTillLtc, ltcAtAge },
    allPhaseCosts,
  } = client;

  // Get the self-funding source from multipleFundingSources or create empty one for new source
  const selfFunding =
    fundingSourceId && multipleFundingSources.selfFunding[fundingSourceId]
      ? multipleFundingSources.selfFunding[fundingSourceId]
      : ({
          hasSelfFunding: false,
          existingAssetsValue: null,
          annualRateOfReturn: null,
          contributionStartYear: new Date().getFullYear(),
          contributionEndYear: ltcAtYear,
          monthlyContributionAmount: null,
          gainsTaxRate: null,
          monthlyIncome: null,
          contributionMonthCount: 0,
        } as SelfFundingSource);

  const requiredKeys: SelfFundingKey[] = clientHasStartedLtc
    ? ['existingAssetsValue']
    : ['existingAssetsValue', 'annualRateOfReturn', 'gainsTaxRate'];
  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const [selfFundingState, setSelfFundingState] = useState(
    isAdvisorSession ? { ...selfFunding, addedByAdvisorId: advisorId } : (selfFunding ?? null),
  );
  const [formErrors, setFormErrors] = useState({} as Record<SelfFundingKey, string>);
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const [initialSelfFundingState, setInitialSelfFundingState] = useState(
    isAdvisorSession ? { ...selfFunding, addedByAdvisorId: advisorId } : (selfFunding ?? null),
  );
  const [showWarning, setShowWarning] = useState(false);

  const handleChangeSelfFunding = (selfFundingKey: SelfFundingKey) => (value: number) => {
    setSelfFundingState(prevSelfFundingModalState => {
      return {
        ...prevSelfFundingModalState,
        [selfFundingKey]: value,
      };
    });
  };

  const handleChangeSelfFundingName = (value: string) => {
    setSelfFundingState(prevSelfFundingModalState => {
      return {
        ...prevSelfFundingModalState,
        selfFundingName: value,
      };
    });
  };

  const validateForm = () => {
    let isFormValid = true;
    const errors = {} as Record<SelfFundingKey, string>;
    requiredKeys.forEach(key => {
      const currentValue = selfFundingState[key];
      if (isNullOrUndefined(currentValue)) {
        errors[key] = 'Required Field';
      }
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      isFormValid = false;
    }
    return isFormValid;
  };

  const handleClickSubmit = () => {
    setFormHasBeenSubmitted(true);
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    const updatedSelfFunding = {
      ...selfFundingState,
      hasSelfFunding: true,
      selfFundingName: selfFundingState.selfFundingName || 'Self-funding',
    };

    // For new sources, pass null for fundingSourceId - let the server generate it
    // For existing sources, pass the fundingSourceId to update
    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: clientId,
        fundingSourceType: 'selfFunding',
        fundingSourceData: updatedSelfFunding as any,
        fundingSourceId: fundingSourceId ?? null, // null for new, or actual ID for edit
      }),
    );

    const newParams = new URLSearchParams(searchParams);
    newParams.set('downloadReport', 'true');
    setSearchParams(newParams, { replace: true });
    setOpen(false);
  };

  const formIsValid = Object.keys(formErrors).length === 0;
  const hasRequiredFieldsErrors = Object.keys(formErrors).some(key =>
    requiredKeys.includes(key as SelfFundingKey),
  );

  const resetForm = () => {
    setSelfFundingState(selfFunding ?? null);
    setFormErrors({} as Record<SelfFundingKey, string>);
  };

  const onClose = () => {
    if (JSON.stringify(selfFundingState) !== JSON.stringify(initialSelfFundingState)) {
      setShowWarning(true);
    } else {
      setOpen(false);
      resetForm();
    }
  };

  const handleConfirmClose = () => {
    setShowWarning(false);
    setOpen(false);
    resetForm();
  };

  const handleCancelClose = () => {
    setShowWarning(false);
  };

  // Validate form again when the form has been submitted and inputs change
  // This allows error messages to go away once the user has fixed the error
  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
  }, [
    selfFundingState.annualRateOfReturn,
    selfFundingState.existingAssetsValue,
    selfFundingState.gainsTaxRate,
  ]);

  useEffect(() => {
    if (open) {
      let openingSelfFundingState = selfFunding
        ? { ...selfFunding, hasSelfFunding: true }
        : ({ hasSelfFunding: false } as SelfFundingSource);

      if (isAdvisorSession) {
        openingSelfFundingState = {
          ...openingSelfFundingState,
          addedByAdvisorId: advisorId,
        };
      }

      setSelfFundingState(openingSelfFundingState);
      setInitialSelfFundingState(openingSelfFundingState);
      setFormHasBeenSubmitted(false);
    }
  }, [open]);

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(selfFundingState, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    allPhaseCareMonthsNeeded: allPhaseCosts?.allPhaseCareMonthsNeeded,
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const { flags } = useFeatureFlags();
  const showPreview =
    flags.selfFundingGrowthPreview === true &&
    !clientHasStartedLtc &&
    projectedValue != null &&
    selfFundingState != null;

  return (
    <>
      <UnsavedChangesDialog
        showWarning={showWarning}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
      />

      <DialogModal
        open={open}
        onClose={onClose}
        title="Self-Funding Details"
        subTitle=""
        width="md"
        primaryButton={
          <Button
            type="button"
            className="mb-14 px-12 py-2 text-lg font-semibold text-white sm:mb-0 sm:ml-3 sm:w-auto"
            onClick={handleClickSubmit}
          >
            Submit
          </Button>
        }
      >
        <div className="relative pb-8">
          {showPreview && (
            <Alert
              variant="info"
              className="sticky top-0 z-10 mt-4 bg-[#F8F6FD]"
            >
              <Info className="size-4" />
              <AlertDescription>
                <span className="whitespace-nowrap">
                  {ageAtLtc != null
                    ? `Projected value by age ${ageAtLtc}: `
                    : `Projected value by ${ltcAtYear}: `}
                  <span className="font-semibold">{formatCompactCurrency(projectedValue)}</span>
                </span>
              </AlertDescription>
            </Alert>
          )}
          <div data-intercom-target="Self Funding Fields">
            <SelfFundingField
              title="Self-Funding Name"
              description="Optional name to identify this self-funding source"
            >
              <div className="col-span-1">
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-neutral-800">
                  NAME (OPTIONAL)
                </label>
                <Input
                  type="text"
                  placeholder="Self-funding"
                  value={selfFundingState.selfFundingName ?? ''}
                  onChange={e => handleChangeSelfFundingName(e.target.value)}
                  className="h-12 w-full border-neutral-700 text-base placeholder:text-neutral-400 focus-visible:ring-transparent"
                />
              </div>
            </SelfFundingField>

            <SelfFundingField
              title="Available Assets for LTC Costs"
              description="Present-day value of assets that can be reserved for future long-term care expenses"
            >
              <CurrencyInput
                label="VALUE OF AVAILABLE ASSETS"
                placeholder="Enter dollars"
                value={selfFundingState.existingAssetsValue}
                onChange={handleChangeSelfFunding('existingAssetsValue')}
                onPreview={handleChangeSelfFunding('existingAssetsValue')}
              />
              <ErrorMessage message={formErrors.existingAssetsValue} />
            </SelfFundingField>

            {!clientHasStartedLtc && (
              <>
                <SelfFundingField
                  title="Monthly Contribution"
                  description="Money added each month to assets or investments, aimed at increasing the total value available to fund long-term care expenses."
                >
                  <CurrencyInput
                    label="MONTHLY CONTRIBUTION"
                    placeholder="Enter dollars"
                    value={selfFundingState.monthlyContributionAmount}
                    onChange={handleChangeSelfFunding('monthlyContributionAmount')}
                    onPreview={handleChangeSelfFunding('monthlyContributionAmount')}
                  />
                  <ErrorMessage message={formErrors.monthlyContributionAmount} />
                </SelfFundingField>

                <SelfFundingField
                  title="Annual Rate of Return"
                  description="The expected annual rate of return applied to both existing assets amount and monthly contributions."
                >
                  <PercentInput
                    label="ANNUAL RATE OF RETURN"
                    placeholder="Enter percent"
                    value={selfFundingState.annualRateOfReturn}
                    onChange={handleChangeSelfFunding('annualRateOfReturn')}
                    onPreview={handleChangeSelfFunding('annualRateOfReturn')}
                  />
                  <ErrorMessage message={formErrors.annualRateOfReturn} />
                </SelfFundingField>

                <SelfFundingField
                  title="Gains Tax Rate"
                  description={
                    <>
                      This is the tax rate you will pay on the profit you make from selling your
                      investment(s). For more information, view this{' '}
                      <a
                        href="https://www.irs.gov/taxtopics/tc409"
                        target="_blank"
                        rel="noreferrer"
                        className="text-purple underline"
                      >
                        IRS article
                      </a>
                      .
                    </>
                  }
                >
                  <PercentInput
                    label="GAINS TAX RATE"
                    placeholder="Enter percent"
                    value={selfFundingState.gainsTaxRate}
                    onChange={handleChangeSelfFunding('gainsTaxRate')}
                    onPreview={handleChangeSelfFunding('gainsTaxRate')}
                  />
                  <ErrorMessage message={formErrors.gainsTaxRate} />
                </SelfFundingField>
              </>
            )}

            <SelfFundingField
              title="Future Monthly Income"
              description="Future expected income during long-term care (LTC) years, such as social security, pension, or rental income, that can be used to cover LTC costs. Only include the portion of income that can be allocated to LTC expenses."
              additionalDescription="If the income schedule is not monthly, please calculate and enter the equivalent monthly amount."
              isLastField={true}
            >
              <CurrencyInput
                label="MONTHLY INCOME AMOUNT"
                placeholder="Enter dollars"
                value={selfFundingState.monthlyIncome}
                onChange={handleChangeSelfFunding('monthlyIncome')}
                onPreview={handleChangeSelfFunding('monthlyIncome')}
              />
            </SelfFundingField>
          </div>

          {!formIsValid && (
            <FormErrorAlert mainErrorMessage="There is an error with your submission">
              {hasRequiredFieldsErrors && (
                <li>
                  <span className="font-bold">Required field:</span> Please fill out all required
                  fields.
                </li>
              )}
            </FormErrorAlert>
          )}
        </div>
      </DialogModal>
    </>
  );
}
